import { useId, useState } from 'react';

import type { learningLabSchema } from '@mio/shared';
import type { ReactNode } from 'react';
import type { z } from 'zod';

type Exploration = NonNullable<z.infer<typeof learningLabSchema>['exploration']>;

/** Notes are a learner's observations, not scored answers or simulated measurements. */
export function DiscoveryExperiment({
  exploration,
  children,
}: {
  exploration: Exploration;
  children: ReactNode;
}) {
  const id = useId();
  const [round, setRound] = useState(0);
  const [notes, setNotes] = useState<Record<number, { prediction: string; observation: string }>>(
    {},
  );
  const [revealed, setRevealed] = useState<number[]>([]);
  const [reset, setReset] = useState(0);
  const current = exploration.rounds[round];
  if (!current) return null;
  const note = notes[round] ?? { prediction: '', observation: '' };
  function record(field: 'prediction' | 'observation', value: string) {
    setNotes({ ...notes, [round]: { ...note, [field]: value } });
  }
  return (
    <>
      <div className="discovery-question">
        <strong>{exploration.question}</strong>
      </div>
      <div className="discovery-rounds" aria-label="Experiment rounds">
        {exploration.rounds.map((item, index) => (
          <button key={item.title} aria-pressed={round === index} onClick={() => setRound(index)}>
            <span>{index + 1}</span> {item.title}
          </button>
        ))}
      </div>
      <div className="discovery-brief">
        <p>
          <strong>Try this:</strong> {current.action}
        </p>
        <label htmlFor={`${id}-prediction`}>
          <strong>Predict:</strong> {current.predict}
        </label>
        <input
          id={`${id}-prediction`}
          value={note.prediction}
          onChange={(e) => record('prediction', e.target.value)}
          placeholder="Your prediction (optional)"
        />
      </div>
      <div className="discovery-model" key={reset}>
        {children}
      </div>
      <div className="discovery-notes">
        <label htmlFor={`${id}-observation`}>What changed? What stayed the same?</label>
        <textarea
          id={`${id}-observation`}
          rows={2}
          value={note.observation}
          onChange={(e) => record('observation', e.target.value)}
          placeholder="Record the evidence you see (optional)"
        />
        <div className="lab-actions">
          <button
            aria-expanded={revealed.includes(round)}
            aria-controls={`${id}-explanation`}
            onClick={() =>
              setRevealed(
                revealed.includes(round)
                  ? revealed.filter((r) => r !== round)
                  : [...revealed, round],
              )
            }
          >
            {revealed.includes(round) ? 'Hide explanation' : 'Compare with the explanation'}
          </button>
          <button onClick={() => setReset(reset + 1)}>Reset model</button>
          <button
            disabled={round === exploration.rounds.length - 1}
            onClick={() => setRound(round + 1)}
          >
            Next experiment
          </button>
        </div>
        {revealed.includes(round) && (
          <div id={`${id}-explanation`} className="discovery-explanation">
            <strong>Why this happened</strong>
            <p>{current.explanation}</p>
          </div>
        )}
        <details>
          <summary>
            My experiment notes ·{' '}
            {Object.values(notes).filter((n) => n.prediction || n.observation).length} recorded
          </summary>
          {exploration.rounds.map((item, index) => (
            <div className="discovery-note" key={item.title}>
              <strong>{item.title}</strong>
              <p>Predicted: {notes[index]?.prediction ?? '—'}</p>
              <p>Observed: {notes[index]?.observation ?? '—'}</p>
            </div>
          ))}
          <small>
            Notes stay here while this lesson is open. Copy anything you want to keep before
            leaving.
          </small>
        </details>
        <p className="discovery-transfer">
          <strong>Try to break your new rule:</strong> {exploration.transfer}
        </p>
      </div>
    </>
  );
}
